import {escapeHtml as escapePrintValue} from "../../utils/escapeHtml";
import React, { useMemo } from "react";

import {
  formatMoney,
  toAmount,
} from "../../services/billingService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDateTime = (value) => {
  if (!value) {
    return new Date().toLocaleString("en-IN");
  }

  let date;

  if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (value?.seconds) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (!date || Number.isNaN(date.getTime())) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPrimaryGuest = (stay = {}) => {
  if (
    Array.isArray(stay.guests) &&
    stay.guests.length > 0
  ) {
    return (
      stay.guests.find(
        (guest) => guest?.isPrimary === true
      ) ||
      stay.guests[0]
    );
  }

  return {
    name: stay.guestName || "",
    phoneNumber: stay.phoneNumber || "",
    address: stay.address || "",
  };
};

const getPaymentPurpose = (payment = {}) => {
  const title = clean(payment.title);
  const note = clean(payment.note);
  const source = clean(payment.source);

  if (title && title !== "Payment Received") {
    return title;
  }

  if (
    source === "check_in_advance" ||
    note === "Check-In Advance"
  ) {
    return "Check-In Advance";
  }

  if (source === "pending_payment") {
    return "Pending Amount Received";
  }

  if (source === "checkout_payment") {
    return "Checkout Payment";
  }

  return title || "Payment Received";
};

/* -------------------------------------------------------------------------- */
/*                            Amount in Words                                 */
/* -------------------------------------------------------------------------- */

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

const convertBelowHundred = (number) => {
  const value = Math.floor(number);

  if (value < 20) {
    return ONES[value];
  }

  const tens = TENS[Math.floor(value / 10)];
  const ones = ONES[value % 10];

  return [tens, ones].filter(Boolean).join(" ");
};

const convertBelowThousand = (number) => {
  const value = Math.floor(number);

  if (value < 100) {
    return convertBelowHundred(value);
  }

  const hundredPart = Math.floor(value / 100);
  const remainder = value % 100;

  return [
    `${ONES[hundredPart]} Hundred`,
    remainder
      ? convertBelowHundred(remainder)
      : "",
  ]
    .filter(Boolean)
    .join(" ");
};

const amountToWords = (value) => {
  const amount = Math.max(0, toAmount(value));
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);

  if (rupees === 0 && paise === 0) {
    return "Zero Rupees Only";
  }

  let remaining = rupees;
  const parts = [];

  const crore = Math.floor(
    remaining / 10000000
  );

  if (crore > 0) {
    parts.push(
      `${convertBelowThousand(crore)} Crore`
    );

    remaining %= 10000000;
  }

  const lakh = Math.floor(
    remaining / 100000
  );

  if (lakh > 0) {
    parts.push(
      `${convertBelowThousand(lakh)} Lakh`
    );

    remaining %= 100000;
  }

  const thousand = Math.floor(
    remaining / 1000
  );

  if (thousand > 0) {
    parts.push(
      `${convertBelowThousand(
        thousand
      )} Thousand`
    );

    remaining %= 1000;
  }

  if (remaining > 0) {
    parts.push(
      convertBelowThousand(remaining)
    );
  }

  const rupeeWords = parts.length
    ? `${parts.join(" ")} Rupees`
    : "Zero Rupees";

  const paiseWords =
    paise > 0
      ? ` and ${convertBelowHundred(
          paise
        )} Paise`
      : "";

  return `${rupeeWords}${paiseWords} Only`;
};

/* -------------------------------------------------------------------------- */
/*                              Print HTML                                    */
/* -------------------------------------------------------------------------- */

const buildReceiptHtml = ({
  stay,
  payment,
  hotelName,
  receiptNumber,
}) => {
  const primaryGuest =
    getPrimaryGuest(stay);

  const amount = toAmount(payment?.amount);

  const paymentDate =
    payment?.addedAt ||
    payment?.receivedAt ||
    new Date().toISOString();

  const transactionReference = clean(
    payment?.transactionReference ||
      payment?.transactionId ||
      payment?.reference
  );

  const receivedBy = clean(
    payment?.receivedBy ||
      payment?.createdBy ||
      "Manager"
  );

  return `
    <!DOCTYPE html>

    <html lang="en">
      <head>
        <meta charset="UTF-8" />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        />

        <title>${escapeHtml(
          receiptNumber
        )}</title>

        <style>
          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #0f172a;
            font-family:
              Arial,
              Helvetica,
              sans-serif;
          }

          body {
            padding: 10mm;
          }

          .receipt {
            width: 100%;
            max-width: 175mm;
            margin: 0 auto;
            border: 1.5px solid #0f172a;
          }

          .header {
            padding: 17px;
            text-align: center;
            border-bottom: 2px solid #0f172a;
          }

          .hotel-name {
            margin: 0;
            font-size: 24px;
            font-weight: 900;
            text-transform: uppercase;
          }

          .document-title {
            margin-top: 6px;
            color: #475569;
            font-size: 12px;
            font-weight: 900;
            letter-spacing: 1px;
          }

          .receipt-number {
            margin-top: 7px;
            font-size: 10px;
            font-weight: 800;
          }

          .body {
            padding: 18px;
          }

          .details-grid {
            display: grid;
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
            gap: 10px;
          }

          .detail {
            min-height: 56px;
            padding: 9px;
            border: 1px solid #cbd5e1;
          }

          .label {
            display: block;
            margin-bottom: 5px;
            color: #64748b;
            font-size: 9px;
            font-weight: 900;
            letter-spacing: .4px;
            text-transform: uppercase;
          }

          .value {
            display: block;
            color: #0f172a;
            font-size: 12px;
            font-weight: 800;
            overflow-wrap: anywhere;
          }

          .amount-box {
            margin-top: 16px;
            padding: 17px;
            border: 2px solid #16a34a;
            background: #f0fdf4;
            text-align: center;
          }

          .amount-label {
            color: #166534;
            font-size: 11px;
            font-weight: 900;
            text-transform: uppercase;
          }

          .amount {
            margin-top: 7px;
            color: #166534;
            font-size: 29px;
            font-weight: 900;
          }

          .amount-words {
            margin-top: 7px;
            color: #334155;
            font-size: 10px;
            font-weight: 700;
            line-height: 1.5;
          }

          .note-box {
            margin-top: 14px;
            padding: 10px;
            border: 1px solid #cbd5e1;
            font-size: 11px;
            line-height: 1.5;
          }

          .signature-grid {
            display: grid;
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
            gap: 65px;
            margin-top: 50px;
          }

          .signature {
            text-align: center;
            color: #334155;
            font-size: 10px;
          }

          .signature-line {
            margin-bottom: 6px;
            border-top: 1px solid #111827;
          }

          .footer {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px dashed #94a3b8;
            color: #64748b;
            font-size: 9px;
            line-height: 1.5;
            text-align: center;
          }

          @media print {
            body {
              padding: 0;
            }

            .receipt {
              max-width: none;
            }

            @page {
              size: A4 portrait;
              margin: 18mm;
            }
          }
        </style>
      </head>

      <body>
        <main class="receipt">
          <header class="header">
            <h1 class="hotel-name">
              ${escapeHtml(
                hotelName || "Hotel"
              )}
            </h1>

            <div class="document-title">
              PAYMENT RECEIPT
            </div>

            <div class="receipt-number">
              Receipt No:
              ${escapeHtml(receiptNumber)}
            </div>
          </header>

          <section class="body">
            <div class="details-grid">
              <div class="detail">
                <span class="label">
                  Receipt Date
                </span>

                <strong class="value">
                  ${escapeHtml(
                    formatDateTime(paymentDate)
                  )}
                </strong>
              </div>

              <div class="detail">
                <span class="label">
                  Payment Purpose
                </span>

                <strong class="value">
                  ${escapeHtml(
                    getPaymentPurpose(payment)
                  )}
                </strong>
              </div>

              <div class="detail">
                <span class="label">
                  Guest Name
                </span>

                <strong class="value">
                  ${escapeHtml(
                    primaryGuest?.name ||
                      stay?.guestName ||
                      "Guest"
                  )}
                </strong>
              </div>

              <div class="detail">
                <span class="label">
                  Phone Number
                </span>

                <strong class="value">
                  ${escapeHtml(
                    primaryGuest?.phoneNumber ||
                      stay?.phoneNumber ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail">
                <span class="label">
                  Room Number
                </span>

                <strong class="value">
                  ${escapeHtml(
                    stay?.room || "-"
                  )}
                </strong>
              </div>

              <div class="detail">
                <span class="label">
                  A&D Register Serial
                </span>

                <strong class="value">
                  ${escapeHtml(
                    stay?.adRegisterSerialNo ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail">
                <span class="label">
                  Payment Mode
                </span>

                <strong class="value">
                  ${escapeHtml(
                    payment?.paymentMode ||
                      stay?.paymentMode ||
                      "-"
                  )}
                </strong>
              </div>

              <div class="detail">
                <span class="label">
                  Transaction Reference
                </span>

                <strong class="value">
                  ${escapeHtml(
                    transactionReference || "-"
                  )}
                </strong>
              </div>
            </div>

            <div class="amount-box">
              <div class="amount-label">
                Amount Received
              </div>

              <div class="amount">
                ${escapeHtml(
                  formatMoney(amount)
                )}
              </div>

              <div class="amount-words">
                ${escapeHtml(
                  amountToWords(amount)
                )}
              </div>
            </div>

            ${
              escapePrintValue(clean(payment?.note))
                ? `
                  <div class="note-box">
                    <strong>Note:</strong>
                    ${escapeHtml(payment.note)}
                  </div>
                `
                : ""
            }

            <section class="signature-grid">
              <div class="signature">
                <div class="signature-line"></div>
                Guest Signature
              </div>

              <div class="signature">
                <div class="signature-line"></div>
                Received By:
                ${escapeHtml(receivedBy)}
              </div>
            </section>

            <div class="footer">
              This is a computer-generated payment
              receipt. Payment is subject to final
              reconciliation with the guest folio and
              hotel records.
            </div>
          </section>
        </main>

        <script>
          window.onload = function () {
            setTimeout(function () {
              window.focus();
              window.print();
            }, 300);
          };
        </script>
      </body>
    </html>
  `;
};

/* -------------------------------------------------------------------------- */
/*                         Payment Receipt Modal                              */
/* -------------------------------------------------------------------------- */

function PaymentReceiptModal({
  stay,
  payment,
  hotelName,
  receiptNumber,
  onClose,
}) {
  const primaryGuest = useMemo(
    () => getPrimaryGuest(stay),
    [stay]
  );

  const amount = toAmount(
    payment?.amount
  );

  const paymentDate =
    payment?.addedAt ||
    payment?.receivedAt ||
    new Date().toISOString();

  const stableReceiptNumber =
    clean(
      payment?.receiptNumber ||
        receiptNumber
    ) || "RECEIPT";

  const transactionReference =
    clean(
      payment?.transactionReference ||
        payment?.transactionId ||
        payment?.reference
    );

  if (!stay || !payment) {
    return null;
  }

  const printReceipt = () => {
    const printWindow = window.open(
      "",
      "_blank",
      "width=850,height=850"
    );

    if (!printWindow) {
      alert(
        "Print popup block ho gaya. Browser me popups allow karo."
      );

      return;
    }

    printWindow.document.open();

    printWindow.document.write(
      buildReceiptHtml({
        stay,
        payment,
        hotelName:
          hotelName ||
          stay.hotelName ||
          "Hotel",

        receiptNumber:
          stableReceiptNumber,
      })
    );

    printWindow.document.close();
  };

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          onClose?.();
        }
      }}
    >
      <div style={modalStyle}>
        <div style={toolbarStyle}>
          <button
            type="button"
            onClick={() => onClose?.()}
            style={secondaryButtonStyle}
          >
            Close
          </button>

          <button
            type="button"
            onClick={printReceipt}
            style={primaryButtonStyle}
          >
            🖨 Print / Save PDF
          </button>
        </div>

        <article style={receiptSheetStyle}>
          <header style={headerStyle}>
            <h2 style={hotelNameStyle}>
              {hotelName ||
                stay.hotelName ||
                "Hotel"}
            </h2>

            <div style={documentTitleStyle}>
              PAYMENT RECEIPT
            </div>

            <div style={receiptNumberStyle}>
              Receipt No:{" "}
              {stableReceiptNumber}
            </div>
          </header>

          <section style={detailsGridStyle}>
            <DetailBox
              label="Receipt Date"
              value={formatDateTime(
                paymentDate
              )}
            />

            <DetailBox
              label="Payment Purpose"
              value={getPaymentPurpose(
                payment
              )}
            />

            <DetailBox
              label="Guest Name"
              value={
                primaryGuest?.name ||
                stay.guestName ||
                "Guest"
              }
            />

            <DetailBox
              label="Phone Number"
              value={
                primaryGuest?.phoneNumber ||
                stay.phoneNumber ||
                "-"
              }
            />

            <DetailBox
              label="Room Number"
              value={stay.room || "-"}
            />

            <DetailBox
              label="A&D Serial"
              value={
                stay.adRegisterSerialNo ||
                "-"
              }
            />

            <DetailBox
              label="Payment Mode"
              value={
                payment.paymentMode ||
                stay.paymentMode ||
                "-"
              }
            />

            <DetailBox
              label="Transaction Reference"
              value={
                transactionReference || "-"
              }
            />
          </section>

          <section style={amountBoxStyle}>
            <div style={amountLabelStyle}>
              Amount Received
            </div>

            <div style={amountStyle}>
              {formatMoney(amount)}
            </div>

            <div style={amountWordsStyle}>
              {amountToWords(amount)}
            </div>
          </section>

          {clean(payment.note) && (
            <section style={noteBoxStyle}>
              <strong>Note:</strong>{" "}
              {payment.note}
            </section>
          )}

          <section style={signatureGridStyle}>
            <div style={signatureStyle}>
              <div style={signatureLineStyle} />
              Guest Signature
            </div>

            <div style={signatureStyle}>
              <div style={signatureLineStyle} />
              Received By:{" "}
              {payment.receivedBy ||
                "Manager"}
            </div>
          </section>

          <footer style={footerStyle}>
            Computer-generated hotel payment receipt.
          </footer>
        </article>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Small Component                                 */
/* -------------------------------------------------------------------------- */

function DetailBox({
  label,
  value,
}) {
  return (
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>
        {label}
      </span>

      <strong style={detailValueStyle}>
        {value}
      </strong>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 23000,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.74)",
};

const modalStyle = {
  width: "min(720px,100%)",
  maxHeight: "95vh",

  overflowY: "auto",

  padding: 13,

  borderRadius: 17,

  background: "#e2e8f0",
};

const toolbarStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,

  display: "flex",
  justifyContent: "flex-end",

  gap: 9,

  marginBottom: 11,
};

const receiptSheetStyle = {
  minHeight: 520,

  padding: 28,

  background: "#ffffff",
  color: "#0f172a",

  border: "1px solid #94a3b8",

  boxSizing: "border-box",
};

const headerStyle = {
  paddingBottom: 14,

  borderBottom:
    "2px solid #0f172a",

  textAlign: "center",
};

const hotelNameStyle = {
  margin: 0,

  fontSize: 24,
  textTransform: "uppercase",
};

const documentTitleStyle = {
  marginTop: 5,

  color: "#475569",

  fontSize: 11,
  fontWeight: 900,
  letterSpacing: 1,
};

const receiptNumberStyle = {
  marginTop: 6,

  color: "#334155",

  fontSize: 10,
  fontWeight: 800,
};

const detailsGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(210px,1fr))",

  gap: 9,

  marginTop: 15,
};

const detailBoxStyle = {
  minHeight: 56,

  padding: 9,

  border:
    "1px solid #cbd5e1",
};

const detailLabelStyle = {
  display: "block",

  marginBottom: 5,

  color: "#64748b",

  fontSize: 9,
  fontWeight: 900,

  textTransform: "uppercase",
};

const detailValueStyle = {
  display: "block",

  color: "#0f172a",

  fontSize: 11,
  overflowWrap: "anywhere",
};

const amountBoxStyle = {
  marginTop: 16,

  padding: 17,

  border: "2px solid #16a34a",

  background: "#f0fdf4",

  textAlign: "center",
};

const amountLabelStyle = {
  color: "#166534",

  fontSize: 10,
  fontWeight: 900,

  textTransform: "uppercase",
};

const amountStyle = {
  marginTop: 7,

  color: "#166534",

  fontSize: 29,
  fontWeight: 900,
};

const amountWordsStyle = {
  marginTop: 7,

  color: "#334155",

  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1.5,
};

const noteBoxStyle = {
  marginTop: 14,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  color: "#334155",

  fontSize: 11,
};

const signatureGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(2,minmax(0,1fr))",

  gap: 65,

  marginTop: 55,
};

const signatureStyle = {
  color: "#334155",

  fontSize: 10,
  textAlign: "center",
};

const signatureLineStyle = {
  marginBottom: 6,

  borderTop:
    "1px solid #111827",
};

const footerStyle = {
  marginTop: 20,

  paddingTop: 10,

  borderTop:
    "1px dashed #94a3b8",

  color: "#64748b",

  fontSize: 9,
  textAlign: "center",
};

const commonButtonStyle = {
  border: 0,

  borderRadius: 9,

  padding: "9px 13px",

  color: "#ffffff",

  fontWeight: 800,
  cursor: "pointer",
};

const primaryButtonStyle = {
  ...commonButtonStyle,
  background: "#16a34a",
};

const secondaryButtonStyle = {
  ...commonButtonStyle,
  background: "#475569",
};

export default PaymentReceiptModal;